/**
 * First-party behavior tracking: which screens people open, how long they stay, where their
 * visit ends, forms they start and leave, and named steps (`track('deal_accepted')`). Events
 * are batched to POST /api/events (the backend's app_events) and read on the admin Behavior
 * page. Nothing goes to a third party.
 *
 * Privacy: screens are sent as route patterns ('/deals/:id'); a form abandon names the form and
 * the last field touched, never what was typed; tokens in addresses are never sent.
 *
 * Use: call `usePageTracking('athlete')` once inside the router (the root layout).
 */
import { useEffect } from 'react'
import { useLocation, useMatches } from 'react-router-dom'
import { getAccessToken, getAuthConfig } from '../auth/authStore'

type EventType = 'page_view' | 'page_leave' | 'action' | 'form_abandon'
type Props = Record<string, string | number | boolean | null>
type QueuedEvent = {
  type: EventType
  name: string
  path?: string
  props?: Props
  durationMs?: number
  at: string
}

const FLUSH_MS = 10_000
const FLUSH_AT = 20
const SESSION_IDLE_MS = 30 * 60_000

let app = ''
let queue: QueuedEvent[] = []
let timer: ReturnType<typeof setTimeout> | null = null

const randomId = () => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  } catch {}
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/** A random id for this browser, kept so a visit before sign-up joins the account made from it */
function visitorId() {
  try {
    let id = localStorage.getItem('athios_vid')
    if (!id) localStorage.setItem('athios_vid', (id = randomId()))
    return id
  } catch {
    return (visitorFallback ??= randomId())
  }
}
let visitorFallback: string | undefined

/** One tab's visit; a new one after 30 minutes with nothing happening */
function sessionId() {
  const now = Date.now()
  try {
    const saved = JSON.parse(sessionStorage.getItem('athios_sid') || 'null')
    const id = saved && now - saved.last < SESSION_IDLE_MS ? saved.id : randomId()
    sessionStorage.setItem('athios_sid', JSON.stringify({ id, last: now }))
    return id
  } catch {
    return (sessionFallback ??= randomId())
  }
}
let sessionFallback: string | undefined

function flush(keepalive = false) {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  if (!app || !queue.length) return
  const events = queue.splice(0, 50)
  const token = getAccessToken()
  const { apiBaseUrl } = getAuthConfig()
  try {
    fetch(`${apiBaseUrl}/events`, {
      method: 'POST',
      keepalive,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ app, visitorId: visitorId(), sessionId: sessionId(), events }),
    }).catch(() => {})
  } catch {}
  if (queue.length) flush(keepalive)
}

function enqueue(e: Omit<QueuedEvent, 'at'>) {
  if (!app) return
  queue.push({ ...e, at: new Date().toISOString() })
  if (queue.length >= FLUSH_AT) flush()
  else if (!timer) timer = setTimeout(() => flush(), FLUSH_MS)
}

/** A named step: track('deal_accepted', { dealId }) */
export function track(name: string, props?: Props) {
  enqueue({ type: 'action', name, path: current?.name ?? '', props })
}

// ---- The screen being looked at, and how long it has been visible ----

let current: {
  name: string
  path: string
  pv: string
  visibleMs: number
  since: number | null
} | null = null

const visibleNow = () => typeof document === 'undefined' || document.visibilityState === 'visible'

function startPage(name: string, path: string) {
  current = { name, path, pv: randomId(), visibleMs: 0, since: visibleNow() ? Date.now() : null }
  enqueue({ type: 'page_view', name, path, props: { pv: current.pv } })
}

/** Close the visible stretch on the current screen; `leave` reports it as a page_leave */
function pauseAndReport() {
  if (!current) return
  if (current.since != null) {
    current.visibleMs += Date.now() - current.since
    current.since = null
  }
  if (current.visibleMs > 0) {
    enqueue({
      type: 'page_leave',
      name: current.name,
      path: current.path,
      durationMs: current.visibleMs,
      props: { pv: current.pv },
    })
    current.visibleMs = 0
  }
}

// ---- Forms started and left without submitting ----

type FormState = { key: string; lastField: string; fields: Set<string> }
const started = new Map<string, FormState>()

const fieldName = (el: HTMLElement) =>
  (
    el.getAttribute('name') ||
    el.id ||
    el.getAttribute('aria-label') ||
    el.getAttribute('placeholder') ||
    (el as HTMLInputElement).type ||
    el.tagName.toLowerCase()
  ).slice(0, 60)

const formKey = (el: HTMLElement) => {
  const form = el.closest('form, [data-track-form]')
  return (
    form?.getAttribute('data-track-form') ||
    form?.getAttribute('aria-label') ||
    form?.id ||
    form?.getAttribute('name') ||
    current?.name ||
    location.pathname
  ).slice(0, 100)
}

function reportAbandoned() {
  for (const f of started.values()) {
    enqueue({
      type: 'form_abandon',
      name: f.key,
      path: current?.name ?? '',
      props: { lastField: f.lastField, fieldsTouched: f.fields.size },
    })
  }
  started.clear()
}

/** A form finished some way other than a submit event: don't count it as abandoned */
export function markFormDone(key?: string) {
  if (key) started.delete(key)
  else started.clear()
}

let listening = false
function listen() {
  if (listening || typeof document === 'undefined') return
  listening = true
  document.addEventListener(
    'input',
    (e) => {
      const el = e.target as HTMLElement | null
      if (!el || !(el instanceof HTMLElement) || !el.matches('input, textarea, select')) return
      if (el.closest('[data-track-ignore]')) return
      const key = formKey(el)
      const f = started.get(key) ?? { key, lastField: '', fields: new Set<string>() }
      f.lastField = fieldName(el)
      f.fields.add(f.lastField)
      started.set(key, f)
    },
    true,
  )
  document.addEventListener(
    'submit',
    (e) => {
      const el = e.target as HTMLElement | null
      if (el instanceof HTMLElement) started.delete(formKey(el))
    },
    true,
  )
  // A tap on a form's submit button counts as submitting it (forms that never fire submit)
  document.addEventListener(
    'click',
    (e) => {
      const btn = (e.target as HTMLElement | null)?.closest?.(
        'button[type="submit"], [data-track-submit]',
      )
      if (btn instanceof HTMLElement) started.delete(formKey(btn))
    },
    true,
  )
  document.addEventListener('visibilitychange', () => {
    if (!current) return
    if (document.visibilityState === 'hidden') {
      pauseAndReport()
      flush(true)
    } else if (current.since == null) {
      current.since = Date.now()
      sessionId() // keeps the visit alive, or starts a new one after a long break
    }
  })
  window.addEventListener('pagehide', () => {
    pauseAndReport()
    reportAbandoned()
    flush(true)
  })
}

/** '/deals/kJVLzjQ' → '/deals/:id', from the route's params */
function routePattern(
  pathname: string,
  params: Record<string, string | undefined>,
  keep: string[] = [],
) {
  let pattern = pathname
  const entries = Object.entries(params)
    .filter(([k, v]) => !!v && !keep.includes(k))
    .sort((a, b) => b[1]!.length - a[1]!.length)
  for (const [key, value] of entries) {
    const label = key === '*' ? '*' : `:${key}`
    pattern = pattern
      .replace(`/${value}`, `/${label}`)
      .replace(`/${encodeURIComponent(value!)}`, `/${label}`)
  }
  return pattern.length > 1 ? pattern.replace(/\/$/, '') : pattern
}

/**
 * Track this app's screens. Call once inside the router (in the root layout). Every route
 * change is a page view; leaving a screen reports how long it was visible.
 */
export function usePageTracking(
  appId: string,
  options: {
    /** Params kept as they are, so each value is its own screen (a wizard's `step`) */
    keepParams?: string[]
  } = {},
) {
  const location = useLocation()
  const matches = useMatches()
  const leaf = matches[matches.length - 1]
  const params = (leaf?.params ?? {}) as Record<string, string | undefined>
  const name = routePattern(location.pathname, params, options.keepParams)
  // The raw address only when it holds no secret (magic links, invites carry tokens)
  const hasSecret = Object.keys(params).some((k) => /token|code|secret/i.test(k))
  const path = hasSecret ? name : location.pathname

  useEffect(() => {
    app = appId
    listen()
  }, [appId])

  useEffect(() => {
    if (current?.name === name && current.path === path) return
    pauseAndReport()
    reportAbandoned()
    startPage(name, path)
  }, [name, path])
}
